import React, { useState, useEffect } from 'react';
import { Plus, ArrowRight, Clock, ExternalLink } from 'lucide-react';

const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8000/api'
  : 'https://hitlist.cc/api';

const RECENT_LISTS_KEY = 'hitlist_recent_lists';

function getRecentLists() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_LISTS_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveRecentList(listId, name) {
  try {
    let recent = getRecentLists().filter(l => l.listId !== listId);
    recent.unshift({ listId, name, visitedAt: new Date().toISOString() });
    recent = recent.slice(0, 10);
    localStorage.setItem(RECENT_LISTS_KEY, JSON.stringify(recent));
  } catch {}
}

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const HomePage = ({ onNavigate }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [recentLists, setRecentLists] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    setRecentLists(getRecentLists());
  }, []);

  const createNewList = async (withSamples = false) => {
    setIsCreating(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/lists.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'My New List' }),
      });
      if (!res.ok) throw new Error('Failed to create list');
      const list = await res.json();

      if (withSamples) {
        const samples = [
          'Welcome to your shareable todo list!',
          'Double-click to edit any item',
          'Drag items to reorder them',
          'Share the URL with anyone',
          'Check off completed items',
        ];
        for (const title of samples) {
          await fetch(`${API_BASE}/todos.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ list_id: list.list_id, title }),
          });
        }
      }

      saveRecentList(list.list_id, list.name);
      onNavigate(list.list_id);
    } catch (e) {
      setError('Failed to create list. Is the server running?');
      setIsCreating(false);
    }
  };

  const goToList = () => {
    let input = inputValue.trim();
    if (!input) return;
    // Handle full URLs
    try {
      const url = new URL(input);
      input = url.pathname.replace(/^\//, '').split('/')[0];
    } catch {}
    if (!/^[A-Za-z0-9]{10}$/.test(input)) {
      setError('Invalid list ID. Must be exactly 10 alphanumeric characters.');
      return;
    }
    setError('');
    onNavigate(input);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-700 flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="mb-8 text-center">
        <div className="w-20 h-20 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
          <span className="text-4xl font-black text-white">H</span>
        </div>
        <h1 className="text-5xl font-extrabold text-white tracking-tight">
          Hitlist<span className="text-purple-300">.cc</span>
        </h1>
        <p className="text-indigo-200 mt-2 text-lg">Create and share todo lists instantly</p>
      </div>

      {/* Main card */}
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
        {/* Create buttons */}
        <div className="space-y-3 mb-8">
          <button
            onClick={() => createNewList(true)}
            disabled={isCreating}
            className="w-full py-4 px-6 rounded-2xl font-semibold text-white text-lg flex items-center justify-center gap-3 disabled:opacity-60 transition-opacity"
            style={{ backgroundColor: '#4F46E5' }}
          >
            {isCreating ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Plus size={22} />
            )}
            {isCreating ? 'Creating…' : 'Create New List'}
            {!isCreating && <span className="text-sm font-normal text-indigo-200">(with samples)</span>}
          </button>
          <button
            onClick={() => createNewList(false)}
            disabled={isCreating}
            className="w-full py-4 px-6 rounded-2xl font-semibold text-slate-600 text-lg flex items-center justify-center gap-3 border-2 border-slate-200 hover:border-indigo-300 disabled:opacity-60 transition-colors"
          >
            <Plus size={22} />
            Create Blank List
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-slate-400 font-medium text-sm">OR</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Go to existing list */}
        <div>
          <h2 className="text-xl font-bold text-slate-800 mb-1">Open Existing List</h2>
          <p className="text-slate-500 text-sm mb-4">Enter a 10-digit list ID or paste a full URL</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={e => { setInputValue(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && goToList()}
              placeholder="e.g., fQnagnC8BS"
              className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-400 font-mono"
            />
            <button
              onClick={goToList}
              className="bg-slate-800 text-white px-5 py-3 rounded-xl font-semibold hover:bg-slate-700 transition-colors flex items-center gap-2"
            >
              Go <ArrowRight size={16} />
            </button>
          </div>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>

        {/* Recent lists */}
        {recentLists.length > 0 && (
          <div className="mt-8">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Clock size={14} /> Recent Lists
            </h3>
            <div className="space-y-2">
              {recentLists.map(l => (
                <button
                  key={l.listId}
                  onClick={() => onNavigate(l.listId)}
                  className="w-full text-left px-4 py-3 rounded-xl hover:bg-slate-50 border border-slate-100 flex items-center justify-between group transition-colors"
                >
                  <div>
                    <div className="font-medium text-slate-800 text-sm truncate max-w-xs">{l.name}</div>
                    <div className="text-xs text-slate-400 font-mono">{l.listId} · {timeAgo(l.visitedAt)}</div>
                  </div>
                  <ExternalLink size={14} className="text-slate-300 group-hover:text-indigo-500 transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="text-indigo-300 text-sm mt-6">No signup required · Lists are shareable by URL</p>
    </div>
  );
};

export { saveRecentList };
export default HomePage;
